import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { chatsApi, ChatType } from '../../api/chats';
import { profilesApi } from '../../api/profiles';
import HrnbModal from '../controls/HrnbModal';
import HrnbInput from '../controls/HrnbInput';
import HrnbButton from '../controls/HrnbButton';
import HrnbAlert from '../controls/HrnbAlert';
import HrnbLoader from '../controls/HrnbLoader';
import '../../css/chats/ManageChatModal.css';

/**
 * Модальное окно управления групповым чатом:
 *  — смена названия (admin)
 *  — список участников с удалением (admin)
 *  — добавление участников (admin)
 *  — назначение/снятие админов (admin)
 *  — выход из чата (любой)
 *
 * @param {Object} props
 * @param {boolean} props.open — открыто ли окно
 * @param {Function} props.onClose — колбэк закрытия
 * @param {Object} props.chat — объект чата (ChatResponse)
 * @param {string} props.currentProfileId — ID профиля текущего пользователя
 * @param {Function} props.onChatUpdated — колбэк после любого изменения (updatedChat)
 */
export default function ManageChatModal({ open, onClose, chat, currentProfileId, onChatUpdated }) {
    const [title, setTitle] = useState('');
    const [titleDirty, setTitleDirty] = useState(false);
    const [savingTitle, setSavingTitle] = useState(false);

    const [allProfiles, setAllProfiles] = useState([]);
    const [loadingProfiles, setLoadingProfiles] = useState(false);
    const [addSearchQuery, setAddSearchQuery] = useState('');

    const [actionLoading, setActionLoading] = useState(false);
    const [error, setError] = useState(null);
    const [successMsg, setSuccessMsg] = useState(null);
    const [showClearConfirm, setShowClearConfirm] = useState(false);

    const isAdmin = useMemo(() => {
        if (!chat || !currentProfileId) return false;
        const adminIds = chat.adminIds || chat.admins || [];
        return adminIds.includes(currentProfileId);
    }, [chat, currentProfileId]);

    // Типы чатов, для которых доступно управление
    const isManagedType = chat && chat.chatType !== ChatType.DIRECT;

    // Загрузка данных при открытии
    useEffect(() => {
        if (!open || !chat) return;
        setTitle(chat.title || chat.name || '');
        setTitleDirty(false);
        setError(null);
        setSuccessMsg(null);
        setShowClearConfirm(false);
    }, [open, chat]);

    // Загрузка профилей для добавления участников
    useEffect(() => {
        if (!open || !isAdmin) return;
        let cancelled = false;
        const fetchProfiles = async () => {
            setLoadingProfiles(true);
            try {
                const response = await profilesApi.list({ size: 1000 });
                const list = Array.isArray(response?.content)
                    ? response.content
                    : Array.isArray(response) ? response : [];
                if (!cancelled) setAllProfiles(list);
            } catch (err) {
                console.error('[ManageChatModal] Failed to fetch profiles:', err);
            } finally {
                if (!cancelled) setLoadingProfiles(false);
            }
        };
        fetchProfiles();
        return () => { cancelled = true; };
    }, [open, isAdmin]);

    const participantIds = useMemo(() => {
        if (!chat) return new Set();
        return new Set(chat.participantIds || chat.participants || []);
    }, [chat]);

    const adminIds = useMemo(() => {
        if (!chat) return new Set();
        return new Set(chat.adminIds || chat.admins || []);
    }, [chat]);

    // Профили текущих участников (для отображения)
    const participantProfiles = useMemo(() => {
        return allProfiles.filter(p => participantIds.has(p.id));
    }, [allProfiles, participantIds]);

    // Профили, которых можно добавить (не участники)
    const availableProfiles = useMemo(() => {
        if (!addSearchQuery.trim()) return [];
        const q = addSearchQuery.toLowerCase();
        return allProfiles
            .filter(p => !participantIds.has(p.id) && p.id !== currentProfileId)
            .filter(p => (p.displayName || '').toLowerCase().includes(q))
            .slice(0, 10);
    }, [allProfiles, participantIds, currentProfileId, addSearchQuery]);

    // === Обработчики ===

    const handleSaveTitle = useCallback(async () => {
        if (!title.trim() || !chat) return;
        if (title.trim() === (chat.title || chat.name || '')) {
            setTitleDirty(false);
            return;
        }
        setSavingTitle(true);
        setError(null);
        try {
            const updated = await chatsApi.updateChatTitle(chat.id, title.trim());
            onChatUpdated?.(updated);
            setTitleDirty(false);
            setSuccessMsg('Название обновлено');
            setTimeout(() => setSuccessMsg(null), 2500);
        } catch (err) {
            const msg = err.response?.data?.error || err.message || 'Ошибка обновления названия';
            setError(msg);
        } finally {
            setSavingTitle(false);
        }
    }, [title, chat, onChatUpdated]);

    const handleAddParticipant = useCallback(async (profileId, displayName) => {
        if (!chat) return;
        setActionLoading(true);
        setError(null);
        try {
            const updated = await chatsApi.addParticipant(chat.id, profileId);
            onChatUpdated?.(updated);
            setSuccessMsg(`«${displayName || 'Участник'}» добавлен`);
            setTimeout(() => setSuccessMsg(null), 2500);
        } catch (err) {
            setError(err.response?.data?.error || 'Не удалось добавить участника');
        } finally {
            setActionLoading(false);
        }
    }, [chat, onChatUpdated]);

    const handleRemoveParticipant = useCallback(async (profileId, displayName) => {
        if (!chat) return;
        setActionLoading(true);
        setError(null);
        try {
            const updated = await chatsApi.removeParticipant(chat.id, profileId);
            onChatUpdated?.(updated);
            setSuccessMsg(`«${displayName || 'Участник'}» удалён`);
            setTimeout(() => setSuccessMsg(null), 2500);
        } catch (err) {
            setError(err.response?.data?.error || 'Не удалось удалить участника');
        } finally {
            setActionLoading(false);
        }
    }, [chat, onChatUpdated]);

    const handleToggleAdmin = useCallback(async (profileId, isCurrentlyAdmin) => {
        if (!chat) return;
        setActionLoading(true);
        setError(null);
        try {
            const updated = isCurrentlyAdmin
                ? await chatsApi.removeAdmin(chat.id, profileId)
                : await chatsApi.assignAdmin(chat.id, profileId);
            onChatUpdated?.(updated);
        } catch (err) {
            setError(err.response?.data?.error || 'Не удалось изменить роль администратора');
        } finally {
            setActionLoading(false);
        }
    }, [chat, onChatUpdated]);

    const handleLeaveChat = useCallback(async () => {
        if (!chat) return;
        setActionLoading(true);
        setError(null);
        try {
            await chatsApi.leaveChat(chat.id);
            onClose();
            // ChatsLayout обновит список через WS/polling
        } catch (err) {
            setError(err.response?.data?.error || 'Не удалось покинуть чат');
        } finally {
            setActionLoading(false);
        }
    }, [chat, onClose]);

    const handleClearHistory = useCallback(async () => {
        if (!chat) return;
        setActionLoading(true);
        setError(null);
        try {
            const updated = await chatsApi.clearChatHistory(chat.id);
            onChatUpdated?.(updated);
            setShowClearConfirm(false);
            setSuccessMsg('История переписки очищена');
            setTimeout(() => setSuccessMsg(null), 3000);
        } catch (err) {
            setError(err.response?.data?.error || 'Не удалось очистить историю');
            setShowClearConfirm(false);
        } finally {
            setActionLoading(false);
        }
    }, [chat, onChatUpdated]);

    if (!isManagedType) return null;

    return (
        <HrnbModal open={open} onClose={onClose} title="Управление чатом">
            <div className="manage-chat-modal">
                {/* Смена названия */}
                {isAdmin && (
                    <div className="manage-chat-modal__section">
                        <label className="manage-chat-modal__label">Название чата</label>
                        <div className="manage-chat-modal__title-row">
                            <HrnbInput
                                value={title}
                                onChange={(e) => {
                                    setTitle(e.target.value);
                                    setTitleDirty(true);
                                }}
                                placeholder="Название группы..."
                                maxLength={100}
                                className="manage-chat-modal__title-input"
                            />
                            <HrnbButton
                                onClick={handleSaveTitle}
                                disabled={savingTitle || !titleDirty || !title.trim()}
                            >
                                {savingTitle ? <HrnbLoader size="sm" /> : 'Сохранить'}
                            </HrnbButton>
                        </div>
                    </div>
                )}

                {/* Добавление участников */}
                {isAdmin && (
                    <div className="manage-chat-modal__section">
                        <label className="manage-chat-modal__label">Добавить участника</label>
                        <HrnbInput
                            value={addSearchQuery}
                            onChange={(e) => setAddSearchQuery(e.target.value)}
                            placeholder="Поиск по имени..."
                            className="manage-chat-modal__add-search"
                        />
                        {addSearchQuery.trim() && (
                            <div className="manage-chat-modal__add-list">
                                {loadingProfiles ? (
                                    <div className="manage-chat-modal__loading"><HrnbLoader /></div>
                                ) : availableProfiles.length === 0 ? (
                                    <div className="manage-chat-modal__empty">Подходящих пользователей нет</div>
                                ) : (
                                    availableProfiles.map(profile => (
                                        <div
                                            key={profile.id}
                                            className="manage-chat-modal__add-item"
                                        >
                                            <span className="manage-chat-modal__user-name">
                                                {profile.displayName || 'Пользователь'}
                                            </span>
                                            <HrnbButton
                                                size="sm"
                                                variant="primary"
                                                onClick={() => handleAddParticipant(profile.id, profile.displayName)}
                                                disabled={actionLoading}
                                            >
                                                Добавить
                                            </HrnbButton>
                                        </div>
                                    ))
                                )}
                            </div>
                        )}
                    </div>
                )}

                {/* Список участников */}
                <div className="manage-chat-modal__section">
                    <label className="manage-chat-modal__label">
                        Участники ({participantIds.size})
                    </label>
                    <div className="manage-chat-modal__participants">
                        {participantProfiles.length === 0 && !loadingProfiles ? (
                            <div className="manage-chat-modal__empty">
                                Не удалось загрузить информацию об участниках
                            </div>
                        ) : (
                            participantProfiles.map(profile => {
                                const isProfileAdmin = adminIds.has(profile.id);
                                const isSelf = profile.id === currentProfileId;
                                return (
                                    <div key={profile.id} className="manage-chat-modal__participant">
                                        <div className="manage-chat-modal__participant-info">
                                            <span className="manage-chat-modal__user-name">
                                                {profile.displayName || 'Пользователь'}
                                            </span>
                                            {isSelf && <span className="manage-chat-modal__self-badge">Вы</span>}
                                            {isProfileAdmin && <span className="manage-chat-modal__admin-badge">Админ</span>}
                                        </div>
                                        {isAdmin && !isSelf && (
                                            <div className="manage-chat-modal__participant-actions">
                                                <HrnbButton
                                                    size="sm"
                                                    variant="light"
                                                    onClick={() => handleToggleAdmin(profile.id, isProfileAdmin)}
                                                    disabled={actionLoading}
                                                    title={isProfileAdmin ? 'Снять админа' : 'Назначить админом'}
                                                >
                                                    {isProfileAdmin ? '↓ Снять' : '↑ Админ'}
                                                </HrnbButton>
                                                <HrnbButton
                                                    size="sm"
                                                    variant="danger"
                                                    onClick={() => handleRemoveParticipant(profile.id, profile.displayName)}
                                                    disabled={actionLoading}
                                                    title="Удалить из чата"
                                                >
                                                    ✕
                                                </HrnbButton>
                                            </div>
                                        )}
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>

                {/* Сообщения */}
                {error && <HrnbAlert variant="danger">{error}</HrnbAlert>}
                {successMsg && <HrnbAlert variant="success">{successMsg}</HrnbAlert>}

                {/* Подтверждение очистки истории */}
                {showClearConfirm && (
                    <div className="manage-chat-modal__section">
                        <div className="manage-chat-modal__confirm-box">
                            <p className="manage-chat-modal__confirm-text">
                                Все сообщения будут безвозвратно удалены. Сам чат останется.
                            </p>
                            <div className="manage-chat-modal__confirm-actions">
                                <HrnbButton
                                    variant="secondary"
                                    size="sm"
                                    onClick={() => setShowClearConfirm(false)}
                                    disabled={actionLoading}
                                >
                                    Отмена
                                </HrnbButton>
                                <HrnbButton
                                    variant="danger"
                                    size="sm"
                                    onClick={handleClearHistory}
                                    disabled={actionLoading}
                                >
                                    {actionLoading ? <HrnbLoader size="sm" /> : 'Да, очистить'}
                                </HrnbButton>
                            </div>
                        </div>
                    </div>
                )}

                {/* Выход из чата */}
                <div className="manage-chat-modal__actions">
                    <div className="manage-chat-modal__actions-left">
                        {isAdmin && !showClearConfirm && (
                            <HrnbButton variant="danger" size="sm" onClick={() => setShowClearConfirm(true)} disabled={actionLoading}>
                                Очистить переписку
                            </HrnbButton>
                        )}
                    </div>
                    <div className="manage-chat-modal__actions-right">
                        <HrnbButton variant="danger" onClick={handleLeaveChat} disabled={actionLoading}>
                            Покинуть чат
                        </HrnbButton>
                        <HrnbButton variant="secondary" onClick={onClose}>
                            Закрыть
                        </HrnbButton>
                    </div>
                </div>
            </div>
        </HrnbModal>
    );
}
